<template>
  <div class="fill-height">
    <admin-wrapper
      title-translation-path="admin.integrations"
      :has-buttons="true"
      :inputs="filteredIntegrations"
      :is-fetched="true"
      @change="editSingleSettingHandler"
      @save="saveSettings"
    >
      <template #top>
        <v-row no-gutters>
          <v-spacer />
          <v-col cols="2">
            <data-input
              clearable
              hide-details
              :placeholder="$i18n.t('default.find')"
              prepend-inner-icon="mdi-magnify"
              :data-type="{ name: 'text' }"
              v-model="filterValue"
              @input="filter"
              class="search-filter-value"
            />
          </v-col>
        </v-row>
      </template>
      <template v-for="setting in filteredIntegrations" #[`${setting.name}.title-prepend`]>
        <v-col
          cols="auto"
          v-if="setting.icon"
          :key="setting.name"
          class="px-6 d-flex align-baseline"
          :class="{ 'brand-image__disabled': !allSettings[setting.name] }"
        >
          <img :src="require(`@/assets/images/brands/${setting.icon}.png`)" class="brand-image" />
        </v-col>
      </template>
      <template #[`sms_module_enabled.prepend-bottom`]>
        <sms-settings />
      </template>
      <template #[`smtp_module_enabled.prepend-bottom`]>
        <integrations-mail />
      </template>
      <template
        v-for="(setting, index) in filteredIntegrations.filter(st => st.inputs)"
        #[`${setting.name}.prepend-bottom`]="data"
      >
        <v-row :key="index">
          <v-col>
            <v-row v-for="(input, indexInput) in data.inputs" :key="indexInput" no-gutters>
              <v-col cols="4">
                <data-input
                  v-bind="input"
                  :ref="`customIntegrationInput-${input.name}`"
                  @change="$event => (settingsValues[input.name] = $event)"
                  :disabled="!allSettings[data.name]"
                  :value="allSettings[input.name]"
                  :rules="allSettings[setting.name] ? [v => !!v || 'rules.required'] : []"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
    </admin-wrapper>
  </div>
</template>

<script>
import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';
import SmsSettings from '@/components/SmsSettings';
import IntegrationsMail from '@/components/IntegrationsMail';

import { call, get } from 'vuex-pathify';
import { debounce } from 'lodash';

export default {
  name: 'IntegrationsSettings',
  components: {
    AdminWrapper,
    DataInput,
    SmsSettings,
    IntegrationsMail,
  },
  data: () => ({
    filterValue: '',
    settingsValues: {},
    filteredIntegrations: [],
  }),
  computed: {
    additionalModules: get('admin/additionalModules'),
    allSettings: get('admin/settingsValues'),
    appKind() {
      return process.env.VUE_APP_APP_KIND || 'aquagis';
    },
    libraryKeysSettings() {
      const isAnyModuleEnabled = ['AQUAGIS_MOBILE', 'AQUAGIS_OFFLINE_MOBILE', 'ZDM_MOBILE'].some(
        moduleName => this.additionalModules[moduleName]?.enabled
      );
      return [
        {
          name: 'mapbox_enabled',
          icon: 'mapbox',
          straightText: 'Mapbox',
          isVisible: isAnyModuleEnabled,
          inputs: [
            {
              name: this.appKind === 'zdm' ? 'zdm_mapbox_token' : 'mapbox_token',
              label: this.$i18n.t('admin.mapbox_token'),
              clearable: true,
              dataType: {
                name: 'text',
              },
            },
          ],
        },
        ...(this.appKind === 'aquagis'
          ? [
              {
                name: 'onesignal_api_enabled',
                straightText: 'OneSignal',
                icon: 'onesignal',
                isVisible: isAnyModuleEnabled,
                inputs: [
                  {
                    name: 'onesignal_api_key',
                    label: this.$i18n.t('admin.apiKey'),
                    clearable: true,
                    dataType: {
                      name: 'text',
                    },
                  },
                  {
                    name: 'onesignal_rest_api_key',
                    label: this.$i18n.t('admin.onesignal_rest_api_key'),
                    clearable: true,
                    dataType: {
                      name: 'text',
                    },
                  },
                ],
              },
            ]
          : []),
      ];
    },
    settings() {
      const defaultConfig = {
        titleCols: 5,
        hasDivider: true,
        dataType: {
          name: 'switch',
        },
      };
      const settingsArray = [
        {
          ...defaultConfig,
          name: 'google_maps_api_key_enabled',
          straightText: 'Google Maps API',
          icon: 'googlemaps',
          isVisible: true,
          inputs: [
            {
              name: 'google_maps_api_key',
              label: this.$i18n.t('admin.apiKey'),
              clearable: true,
              dataType: {
                name: 'text',
              },
            },
          ],
        },
        {
          ...defaultConfig,
          name: 'sms_module_enabled',
          straightText: 'SMS Serwer',
          icon: 'sms',
          isVisible: this.additionalModules['SMS']?.enabled,
        },
        {
          ...defaultConfig,
          name: 'smtp_module_enabled',
          straightText: 'SMTP',
          icon: 'smtp',
          isVisible: this.additionalModules['SMTP']?.enabled,
        },
        ...this.libraryKeysSettings.map(st => ({ ...st, ...defaultConfig })),
      ];
      return settingsArray.filter(st => st.isVisible);
    },
  },
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    editSingleSettingHandler(value, name) {
      this.editSingleSetting({
        key: name,
        body: {
          value,
        },
      });
    },
    getFilteredIntegrations() {
      return this.settings.filter(setting =>
        setting.straightText.toLowerCase().includes((this.filterValue ?? '').toLowerCase())
      );
    },
    filter() {
      this.filteredIntegrations = this.getFilteredIntegrations();
    },
    saveSettings() {
      Object.entries(this.settingsValues).forEach(([key, value]) => this.editSingleSettingHandler(value, key));
      this.settingsValues = {};
    },
  },
  created() {
    this.filter = debounce(this.filter, 500);
    this.filteredIntegrations = this.getFilteredIntegrations();
  },
};
</script>

<style lang="scss" scoped>
.brand-image {
  width: 60px;
}
.brand-image__disabled {
  opacity: 0.4;
  filter: grayscale(100%);
}
</style>
